/**
 * @voltxampere/engine — PWL voltage source stamps (Sprint 22 B3)
 *
 * Linear interpolation between user-supplied (time, voltage)
 * breakpoints. Behaves as an independent voltage source whose
 * value is selected by `pwlValueAt(t, points)`:
 *
 *   t ≤ points[0].t                 → points[0].v   (clamp left)
 *   points[i].t ≤ t < points[i+1].t → linear interpolation
 *   t ≥ points[last].t              → points[last].v (clamp right)
 *
 * Breakpoints are JSON-encoded in `component.params.points`
 * (Sprint 22 B3 carrier) — see
 * `packages/components/src/types/pwl-source.ts` for the format.
 *
 * The DC stamp evaluates at `ctx.t = 0` (or whatever the transient
 * step time happens to be); the same body covers transient via
 * the StampDefinition fall-through. AC small-signal is zero.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { defineDCStamp } from "../definition.js";
import { getTerminalNetId } from "../terminal-utils.js";
/**
 * Parse + validate the JSON-encoded breakpoint string carried in
 * `params.points`. Throws ContractViolationError with a precise
 * pointer (component id + offending index/value) on any shape
 * issue — the validator at the components-package boundary
 * catches most of these, but raw fixtures or post-migration
 * breakage would otherwise crash deeper in the solver loop.
 */
export function parsePWLPoints(componentId, raw) {
    if (typeof raw !== "string") {
        throw new ContractViolationError(`pwl-source "${componentId}": params.points must be a JSON string`);
    }
    let decoded;
    try {
        decoded = JSON.parse(raw);
    }
    catch (err) {
        throw new ContractViolationError(`pwl-source "${componentId}": params.points is not valid JSON (${err instanceof Error ? err.message : String(err)})`);
    }
    if (!Array.isArray(decoded)) {
        throw new ContractViolationError(`pwl-source "${componentId}": params.points must decode to an array`);
    }
    if (decoded.length === 0) {
        throw new ContractViolationError(`pwl-source "${componentId}": params.points must have at least one breakpoint`);
    }
    const points = [];
    for (const [i, item] of decoded.entries()) {
        if (typeof item !== "object" || item === null) {
            throw new ContractViolationError(`pwl-source "${componentId}": breakpoint #${String(i)} is not an object`);
        }
        const candidate = item;
        const t = candidate.t;
        const v = candidate.v;
        if (typeof t !== "number" || !Number.isFinite(t) || t < 0) {
            throw new ContractViolationError(`pwl-source "${componentId}": breakpoint #${String(i)} has invalid t=${String(t)}`);
        }
        if (typeof v !== "number" || !Number.isFinite(v)) {
            throw new ContractViolationError(`pwl-source "${componentId}": breakpoint #${String(i)} has invalid v=${String(v)}`);
        }
        if (i > 0) {
            const prev = points[i - 1];
            if (prev !== undefined && t <= prev.t) {
                throw new ContractViolationError(`pwl-source "${componentId}": breakpoint #${String(i)} t=${String(t)} must be > previous t=${String(prev.t)}`);
            }
        }
        points.push({ t, v });
    }
    return points;
}
/**
 * Evaluate the PWL waveform at simulation time `t`. Pure function
 * — exported so the AC stamp's DC-offset path and tests can reuse
 * it without re-parsing parameters.
 */
export function pwlValueAt(t, points) {
    if (points.length === 0) {
        throw new ContractViolationError("pwlValueAt: empty breakpoint list");
    }
    const first = points[0];
    const last = points[points.length - 1];
    if (first === undefined || last === undefined) {
        // Defensive — `points.length >= 1` guard above ensures this is
        // unreachable but TypeScript's narrowing doesn't follow that
        // through.
        throw new ContractViolationError("pwlValueAt: breakpoint indices out of range");
    }
    if (t <= first.t)
        return first.v;
    if (t >= last.t)
        return last.v;
    // Linear scan — the breakpoint count is expected to be small
    // (< 100) so binary search is overkill. If a future user needs
    // 10k+ breakpoints we can add caching of the previous index.
    for (let i = 1; i < points.length; i++) {
        const next = points[i];
        const prev = points[i - 1];
        if (next === undefined || prev === undefined)
            continue;
        if (t < next.t) {
            const span = next.t - prev.t;
            if (span <= 0) {
                // Already validated upstream; this branch is a defensive
                // backstop against post-validation mutation.
                return prev.v;
            }
            const alpha = (t - prev.t) / span;
            return prev.v + (next.v - prev.v) * alpha;
        }
    }
    return last.v; // unreachable — t < last.t already returned
}
export const pwlSourceDCStamp = (ctx, component) => {
    const points = parsePWLPoints(component.id, component.params.points);
    const v = pwlValueAt(ctx.t, points);
    const pos = ctx.nodeIndex(getTerminalNetId(component, "pos"));
    const neg = ctx.nodeIndex(getTerminalNetId(component, "neg"));
    const k = ctx.vSourceIndex(component.id);
    ctx.addY(pos, k, 1);
    ctx.addY(neg, k, -1);
    ctx.addY(k, pos, 1);
    ctx.addY(k, neg, -1);
    ctx.rhs[k] = (ctx.rhs[k] ?? 0) + v * ctx.sourceScale;
};
/** Independent V source — one augmented row carrying branch current. */
export const pwlSourceDCStampDefinition = defineDCStamp("pwl-source", pwlSourceDCStamp, [
    "vsource",
]);
//# sourceMappingURL=pwl-source.js.map